/**
 * Status and feedback primitives: the status pixel and the tags built on it,
 * the "soon" badge, empty states, callouts and the stage list that shows a
 * pipeline's progress. Re-exported through `./primitives.js`.
 */

import type { CSSProperties, ReactNode } from 'react';

import { useLanguage } from '../../i18n/language.js';
import { workspaceCopy } from '../copy.js';
import { CheckIcon } from './icons.js';

/* ---- Status ---------------------------------------------------------- */

/** The wireframe's status pixel: a 7px square keyed by status. */
export type PixStatus =
  | 'needs-input'
  | 'ready-to-choose'
  | 'needs-review'
  | 'analyzing'
  | 'generating'
  | 'complete'
  | 'ready'
  | 'read'
  | 'failed'
  | 'not-connected'
  | 'draft'
  | 'active'
  | 'paused'
  | 'archived'
  | 'card';

export function Pix({ status }: { readonly status: PixStatus }): ReactNode {
  return <span className="mj-ws-pix" data-s={status} aria-hidden="true" />;
}

export function Tag({
  status,
  children,
  accent = false,
  className = '',
}: {
  readonly status?: PixStatus;
  readonly children: ReactNode;
  readonly accent?: boolean;
  readonly className?: string;
}): ReactNode {
  return (
    <span className={`mj-ws-tag${accent ? ' is-accent' : ''} ${className}`.trim()}>
      {status !== undefined ? <Pix status={status} /> : null}
      {children}
    </span>
  );
}

export function SoonBadge(): ReactNode {
  const { language } = useLanguage();
  return <span className="mj-ws-soon">{workspaceCopy(language).common.soon}</span>;
}

/* ---- Content blocks -------------------------------------------------- */

export function EmptyState({
  title,
  body,
  action,
  children,
  roomy = false,
  className = '',
}: {
  readonly title?: ReactNode;
  readonly body?: ReactNode;
  readonly action?: ReactNode;
  readonly children?: ReactNode;
  readonly roomy?: boolean;
  readonly className?: string;
}): ReactNode {
  return (
    <div className={`mj-ws-empty${roomy ? ' is-roomy' : ''} ${className}`.trim()}>
      {children}
      {title !== undefined ? <p className="mj-ws-empty-title">{title}</p> : null}
      {body !== undefined ? <p className="mj-ws-muted mj-ws-sm">{body}</p> : null}
      {action}
    </div>
  );
}

export function Callout({
  children,
  accent = false,
  className = '',
  role,
  style,
}: {
  readonly children: ReactNode;
  readonly accent?: boolean;
  readonly className?: string;
  readonly role?: 'status' | 'alert';
  readonly style?: CSSProperties;
}): ReactNode {
  return (
    <div
      role={role}
      className={`mj-ws-callout${accent ? ' is-accent' : ''} ${className}`.trim()}
      style={style}
    >
      {children}
    </div>
  );
}

export type StageStatus = 'queued' | 'active' | 'complete' | 'failed';

export interface StageItem {
  readonly key: string;
  readonly title: ReactNode;
  readonly desc?: ReactNode | undefined;
  readonly status: StageStatus;
  readonly meta: ReactNode;
  readonly checking?: readonly ReactNode[] | undefined;
  readonly checkingLabel?: ReactNode | undefined;
  readonly children?: ReactNode | undefined;
}

export function StageList({
  stages,
  className = '',
}: {
  readonly stages: readonly StageItem[];
  readonly className?: string;
}): ReactNode {
  return (
    <ol className={`mj-ws-stages ${className}`.trim()}>
      {stages.map((stage, index) => (
        <li key={stage.key} className="mj-ws-stage" data-status={stage.status}>
          <span className="mj-ws-sicon" aria-hidden="true">
            {stage.status === 'complete' ? <CheckIcon /> : String(index + 1)}
          </span>
          <div className="mj-ws-stage-body">
            <div className="mj-ws-stage-head">
              <span className="mj-ws-strong">{stage.title}</span>
              <span className="mj-ws-muted mj-ws-xs">{stage.meta}</span>
            </div>
            {stage.desc !== undefined ? (
              <p className="mj-ws-muted mj-ws-sm mj-ws-stage-desc">{stage.desc}</p>
            ) : null}
            {stage.checking !== undefined && stage.checking.length > 0 ? (
              <details className="mj-ws-details mj-ws-sm">
                <summary>{stage.checkingLabel}</summary>
                <ul className="mj-ws-checklist">
                  {stage.checking.map((item, itemIndex) => (
                    <li key={String(itemIndex)}>{item}</li>
                  ))}
                </ul>
              </details>
            ) : null}
            {stage.children}
            {stage.status === 'active' ? (
              <div className="mj-ws-bar" aria-hidden="true">
                <div className="mj-ws-barfill" />
              </div>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}
